(() => {
  const SHOP =
    (window.CPC_WISHLIST && window.CPC_WISHLIST.shop) ||
    (window.Shopify && window.Shopify.shop) ||
    "canopy-plant-co.myshopify.com";
  const API_BASE =
    window.CPC_WISHLIST_API || `${window.location.origin}/apps/wishlist`;
  const TOKEN_KEY = "cpc_wl_token";
  const GUEST_KEY = "cpc_wl_guest";
  const STORE_KEY = "cpc_wl_store_v1";
  const DRAWER_PAGE_SIZE = 24;
  const LOCAL_STORE_MAX = 2_000_000;

  function storageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function storageRemove(key) {
    try {
      localStorage.removeItem(key);
    } catch (_) {}
  }

  const state = {
    token: storageGet(TOKEN_KEY),
    guestKey: storageGet(GUEST_KEY) || crypto.randomUUID(),
    lists: [],
    activeListId: null,
    email: null,
    pendingNotify: null,
    apiOnline: false,
    /** variantGid / numeric id keys marked as added this session */
    cartAdded: new Set(),
    drawerVisibleCount: DRAWER_PAGE_SIZE,
  };

  storageSet(GUEST_KEY, state.guestKey);

  function loadLocal() {
    try {
      const raw = storageGet(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function saveLocal() {
    try {
      const payload = JSON.stringify({
        guestKey: state.guestKey,
        email: state.email,
        activeListId: state.activeListId,
        lists: state.lists,
      });
      if (payload.length > LOCAL_STORE_MAX) return;
      storageSet(STORE_KEY, payload);
    } catch (_) {}
  }

  function ensureLocalLists() {
    if (!state.lists.length) {
      const id = "local-" + crypto.randomUUID().slice(0, 8);
      state.lists = [
        {
          id,
          name: "My Wishlist",
          listType: "personal",
          items: [],
        },
      ];
      state.activeListId = id;
    }
    if (!state.activeListId) state.activeListId = state.lists[0].id;
    saveLocal();
  }

  function hydrateFromLocal() {
    const stored = loadLocal();
    if (stored?.lists?.length) {
      state.lists = stored.lists;
      state.activeListId = stored.activeListId || stored.lists[0].id;
      state.email = stored.email || state.email;
      if (stored.guestKey) {
        state.guestKey = stored.guestKey;
        storageSet(GUEST_KEY, state.guestKey);
      }
      return true;
    }
    ensureLocalLists();
    return false;
  }

  function clearAuthToken() {
    state.token = null;
    storageRemove(TOKEN_KEY);
  }

  function resetClientWishlistState() {
    state.lists = [];
    state.activeListId = null;
    saveLocal();
  }

  async function api(path, options = {}) {
    const headers = {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    };
    if (state.token) headers["X-Wishlist-Token"] = state.token;
    const url = `${API_BASE.replace(/\/$/, "")}/${path.replace(/^\//, "")}?shop=${encodeURIComponent(SHOP)}`;
    const res = await fetch(url, {
      ...options,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: "omit",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw Object.assign(new Error(data.error || "request_failed"), {
        data,
        status: res.status,
      });
    }
    return data;
  }

  function isLocalId(id) {
    return String(id || "").startsWith("local-") || String(id || "").startsWith("item-");
  }

  async function createApiSession() {
    const created = await api("session", {
      method: "POST",
      body: { shop: SHOP, guestKey: state.guestKey },
    });
    state.token = created.token;
    state.guestKey = created.guestKey || state.guestKey;
    storageSet(TOKEN_KEY, created.token);
    storageSet(GUEST_KEY, state.guestKey);
    const data = await api("lists");
    applyServerLists(data);
    state.apiOnline = true;
    saveLocal();
    return true;
  }

  async function tryApiSession() {
    try {
      if (state.token) {
        try {
          const data = await api("lists");
          applyServerLists(data);
          state.apiOnline = true;
          saveLocal();
          return true;
        } catch (err) {
          // After a full wipe, old tokens/list ids are dead — mint a fresh session.
          if (err?.status === 401 || err?.data?.error === "unauthorized") {
            console.warn("[Canopy Wishlist] stale session after wipe; recreating");
            clearAuthToken();
            resetClientWishlistState();
            return await createApiSession();
          }
          throw err;
        }
      }
      return await createApiSession();
    } catch (err) {
      console.warn("[Canopy Wishlist] session unavailable", err);
      state.apiOnline = false;
      return false;
    }
  }

  function applyServerLists(data) {
    const serverLists = data?.lists || [];
    const pendingLocalItems = collectPendingLocalItems();
    if (serverLists.length) {
      state.lists = serverLists;
      if (
        !state.activeListId ||
        isLocalId(state.activeListId) ||
        !state.lists.find((l) => l.id === state.activeListId)
      ) {
        state.activeListId = state.lists[0].id;
      }
    } else {
      // Server has an identity but no lists yet — drop stale server ids from
      // a previous wiped install so we don't POST to ghost list ids.
      state.lists = (state.lists || []).filter((l) => isLocalId(l.id));
      ensureLocalLists();
    }
    state.email = data?.identity?.email || state.email;
    return pendingLocalItems;
  }

  function collectPendingLocalItems() {
    const pending = [];
    for (const list of state.lists || []) {
      if (!isLocalId(list.id) && !(list.items || []).some((i) => isLocalId(i.id))) {
        continue;
      }
      for (const item of list.items || []) {
        pending.push({
          listName: list.name || "My Wishlist",
          item,
        });
      }
    }
    return pending;
  }

  async function flushPendingLocalItems(pending) {
    if (!state.apiOnline || !pending?.length) return;
    for (const entry of pending) {
      try {
        let list =
          state.lists.find(
            (l) =>
              !isLocalId(l.id) &&
              (l.name || "").toLowerCase() ===
                (entry.listName || "").toLowerCase(),
          ) || state.lists.find((l) => !isLocalId(l.id));
        if (!list) {
          const created = await api("lists", {
            method: "POST",
            body: { name: entry.listName || "My Wishlist", shop: SHOP },
          });
          if (created.list) {
            state.lists = [...state.lists.filter((l) => !isLocalId(l.id)), created.list];
            list = created.list;
            state.activeListId = list.id;
          }
        }
        if (!list || isLocalId(list.id)) continue;
        const already = (list.items || []).some(
          (i) => i.variantGid === entry.item.variantGid,
        );
        if (already) continue;
        const data = await api(`lists/${list.id}/items`, {
          method: "POST",
          body: {
            productGid: entry.item.productGid,
            variantGid: entry.item.variantGid,
            handle: entry.item.handle,
            title: entry.item.title,
            imageUrl: entry.item.imageUrl,
            productUrl: entry.item.productUrl,
            price: entry.item.price,
            available: entry.item.available !== false,
            notifyBackInStock: Boolean(entry.item.notifyBackInStock),
            shop: SHOP,
            ...customerHints(),
          },
        });
        if (data.list) {
          state.lists = state.lists.map((l) =>
            l.id === list.id ? data.list : l,
          );
          state.activeListId = data.list.id;
        }
      } catch (err) {
        console.warn("[Canopy Wishlist] failed to sync local item", err);
      }
    }
    saveLocal();
  }

  async function ensureSession() {
    const hadLocal = hydrateFromLocal();
    const pendingBefore = hadLocal ? collectPendingLocalItems() : [];
    await tryApiSession();
    if (state.apiOnline) {
      await flushPendingLocalItems(pendingBefore);
      if (!state.lists.length || state.lists.every((l) => isLocalId(l.id))) {
        ensureLocalLists();
        // Still online but no server list — create one
        try {
          const created = await api("lists", {
            method: "POST",
            body: { name: "My Wishlist", shop: SHOP },
          });
          if (created.list) {
            state.lists = [created.list];
            state.activeListId = created.list.id;
            saveLocal();
          }
        } catch (_) {}
      }
    } else {
      ensureLocalLists();
    }
    await identifyLoggedInCustomer();
    return { lists: state.lists };
  }

  /** Resolve a real server list id before mutating. */
  async function ensureServerList(list) {
    if (!list) return null;
    if (!state.apiOnline) {
      const ok = await tryApiSession();
      if (!ok) return list;
    }

    // Stale server ids survive a full wipe in localStorage — never trust them
    // until they appear in the current API list payload.
    const known =
      state.lists.find((l) => !isLocalId(l.id) && l.id === list.id) ||
      state.lists.find(
        (l) =>
          !isLocalId(l.id) &&
          (l.name || "").toLowerCase() === (list.name || "").toLowerCase(),
      ) ||
      state.lists.find((l) => !isLocalId(l.id));
    if (known && !isLocalId(known.id)) {
      state.activeListId = known.id;
      saveLocal();
      return known;
    }

    try {
      const created = await api("lists", {
        method: "POST",
        body: { name: list.name || "My Wishlist", shop: SHOP },
      });
      if (created.list) {
        state.lists = [
          created.list,
          ...state.lists.filter((l) => !isLocalId(l.id)),
        ];
        state.activeListId = created.list.id;
        saveLocal();
        return created.list;
      }
    } catch (err) {
      if (err?.status === 401 || err?.data?.error === "unauthorized") {
        clearAuthToken();
        resetClientWishlistState();
        if (await tryApiSession()) {
          return (
            state.lists.find((l) => !isLocalId(l.id)) ||
            (await ensureServerList({ name: list.name || "My Wishlist" }))
          );
        }
      }
      console.warn("[Canopy Wishlist] create list failed", err);
    }
    return list;
  }

  function customerConfig() {
    const cfg = window.CPC_WISHLIST || {};
    let customerId = cfg.customerId || null;
    let customerEmail = cfg.customerEmail || null;
    let customerGid = cfg.customerGid || null;
    let customerName = cfg.customerName || null;
    try {
      const metaId =
        window.ShopifyAnalytics?.meta?.page?.customerId ||
        window.meta?.page?.customerId ||
        window.__st?.cid ||
        null;
      if (!customerId && metaId) customerId = metaId;
    } catch (_) {}
    if (customerId && !customerGid) {
      customerGid = `gid://shopify/Customer/${customerId}`;
    }
    return {
      ...cfg,
      customerId,
      customerEmail,
      customerGid,
      customerName,
    };
  }

  function customerHints() {
    const cfg = customerConfig();
    const hints = {};
    if (cfg.customerEmail) hints.customerEmail = cfg.customerEmail;
    if (cfg.customerGid) hints.customerGid = cfg.customerGid;
    if (cfg.customerId) hints.customerId = cfg.customerId;
    if (state.email && !hints.customerEmail) hints.customerEmail = state.email;
    return hints;
  }

  async function identifyLoggedInCustomer() {
    const cfg = customerConfig();
    if (!cfg.customerEmail && !cfg.customerGid && !cfg.customerId) return;
    if (cfg.customerEmail) {
      state.email = cfg.customerEmail;
      saveLocal();
    }
    if (!state.apiOnline) return;
    try {
      const data = await api("identify", {
        method: "POST",
        body: {
          email: cfg.customerEmail || undefined,
          customerGid: cfg.customerGid || null,
          customerId: cfg.customerId || null,
          shop: SHOP,
        },
      });
      if (data.identity?.email) state.email = data.identity.email;
      if (data.lists?.length) {
        state.lists = data.lists;
        if (!state.lists.find((l) => l.id === state.activeListId)) {
          state.activeListId = state.lists[0].id;
        }
        saveLocal();
        updateHeart();
        syncPdpButtons();
        if (drawerIsVisible()) renderDrawer();
      }
    } catch (_) {}
  }


  function totalCount() {
    return state.lists.reduce((n, l) => n + (l.items?.length || 0), 0);
  }

  function updateHeart() {
    const count = totalCount();
    document.querySelectorAll("[data-cpc-wishlist-heart]").forEach((el) => {
      const btn =
        el.querySelector(".cpc-wl-heart") ||
        el.querySelector(".cpc-wl-side-tab") ||
        el.querySelector("[data-cpc-wl-open]");
      const badge = el.querySelector("[data-cpc-wl-count]");
      if (btn) btn.classList.toggle("is-active", count > 0);
      if (badge) {
        badge.hidden = count === 0;
        badge.textContent = String(count);
      }
    });
    syncCardHearts();
  }

  function findItem(variantGid) {
    for (const list of state.lists) {
      const item = (list.items || []).find(
        (i) =>
          i.variantGid === variantGid ||
          String(i.variantId) === String(variantGid) ||
          String(i.variantId) === String(variantGid).split("/").pop(),
      );
      if (item) return { list, item };
    }
    return null;
  }

  function findItemByProduct(productGid) {
    if (!productGid) return null;
    for (const list of state.lists) {
      const item = (list.items || []).find((i) => i.productGid === productGid);
      if (item) return { list, item };
    }
    return null;
  }

  function syncCardHearts() {
    document.querySelectorAll("[data-cpc-wl-card-btn]").forEach((btn) => {
      const found =
        findItem(btn.dataset.variantGid) ||
        findItemByProduct(btn.dataset.productGid);
      const active = Boolean(found);
      if (btn.classList.contains("is-active") !== active) {
        btn.classList.toggle("is-active", active);
      }
      const label = active ? "Remove from wishlist" : "Add to wishlist";
      if (btn.getAttribute("aria-label") !== label) {
        btn.setAttribute("aria-label", label);
      }
    });

    document.querySelectorAll("[data-cpc-wl-card-notify]").forEach((btn) => {
      const found =
        findItem(btn.dataset.variantGid) ||
        findItemByProduct(btn.dataset.productGid);
      const notified = Boolean(found?.item?.notifyBackInStock);
      if (btn.classList.contains("is-active") !== notified) {
        btn.classList.toggle("is-active", notified);
      }
      const label = notified
        ? "You'll be notified when back in stock"
        : "Notify me when back in stock";
      if (btn.getAttribute("aria-label") !== label) {
        btn.setAttribute("aria-label", label);
      }
    });
  }

  function cardPayloadFromBtn(btn) {
    return {
      productGid: btn.dataset.productGid,
      variantGid: btn.dataset.variantGid,
      variantId: btn.dataset.variantId,
      handle: btn.dataset.handle,
      title: btn.dataset.title,
      price: btn.dataset.price,
      imageUrl: btn.dataset.image,
      productUrl: btn.dataset.url,
      available: btn.dataset.available === "true",
    };
  }

  async function runNotify(payload) {
    const found =
      findItem(payload.variantGid) || findItemByProduct(payload.productGid);
    if (found) {
      found.item.notifyBackInStock = true;
      saveLocal();
      updateHeart();
      syncPdpButtons();
      await setNotify(found.list.id, found.item.id, true);
    } else {
      await addItem(payload, { notify: true });
    }
    openDrawer();
  }

  function emailPromptRoot() {
    return document.querySelector("[data-cpc-wl-email-prompt]");
  }

  function openEmailPrompt() {
    const root = emailPromptRoot();
    if (!root) return;
    root.hidden = false;
    const input = root.querySelector("[data-cpc-wl-email-prompt-input]");
    if (input) {
      input.value = state.email || "";
      setTimeout(() => input.focus(), 50);
    }
  }

  function closeEmailPrompt() {
    const root = emailPromptRoot();
    if (!root) return;
    root.hidden = true;
  }

  function bindEmailPrompt() {
    const root = emailPromptRoot();
    if (!root || root.dataset.cpcBound === "1") return;
    root.dataset.cpcBound = "1";

    root
      .querySelector("[data-cpc-wl-email-prompt-close]")
      ?.addEventListener("click", () => {
        state.pendingNotify = null;
        closeEmailPrompt();
      });

    root
      .querySelector("[data-cpc-wl-email-prompt-confirm]")
      ?.addEventListener("click", async () => {
        const input = root.querySelector("[data-cpc-wl-email-prompt-input]");
        const email = input?.value?.trim();
        if (!email) return;
        state.email = email;
        saveLocal();
        if (state.apiOnline) {
          try {
            const data = await api("identify", {
              method: "POST",
              body: {
                email,
                shop: SHOP,
                customerGid: customerConfig().customerGid || null,
              },
            });
            if (data.lists?.length) {
              state.lists = data.lists;
              if (!state.lists.find((l) => l.id === state.activeListId)) {
                state.activeListId = state.lists[0].id;
              }
              saveLocal();
              updateHeart();
              syncPdpButtons();
            }
          } catch (_) {}
        }
        closeEmailPrompt();
        if (state.pendingNotify) {
          const fn = state.pendingNotify;
          state.pendingNotify = null;
          await fn();
        }
      });
  }

  function activeList() {
    return state.lists.find((l) => l.id === state.activeListId) || state.lists[0];
  }

  function variantNumericId(item) {
    if (item.variantId != null && item.variantId !== "") {
      return Number(item.variantId);
    }
    const fromGid = String(item.variantGid || "").split("/").pop();
    return fromGid ? Number(fromGid) : null;
  }

  function cartItemKey(item) {
    const gid = item?.variantGid || null;
    const num = variantNumericId(item);
    return { gid, num: num != null && !Number.isNaN(num) ? String(num) : null };
  }

  function markCartAdded(item) {
    const { gid, num } = cartItemKey(item);
    if (gid) state.cartAdded.add(gid);
    if (num) state.cartAdded.add(num);
  }

  function isCartAdded(item) {
    const { gid, num } = cartItemKey(item);
    return Boolean(
      (gid && state.cartAdded.has(gid)) || (num && state.cartAdded.has(num)),
    );
  }

  function cartButtonLabel(item) {
    return isCartAdded(item) ? "Added to cart" : "Add to cart";
  }

  async function syncCartAddedFromCart() {
    try {
      const res = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const cart = await res.json();
      for (const line of cart.items || []) {
        if (line.variant_id != null) {
          state.cartAdded.add(String(line.variant_id));
        }
        if (line.id != null) state.cartAdded.add(String(line.id));
      }
    } catch (_) {}
  }

  async function addToCart(item, btn) {
    const id = variantNumericId(item);
    if (!id || Number.isNaN(id)) {
      console.error("[Canopy Wishlist] missing variant id", item);
      return;
    }

    if (btn) {
      btn.disabled = true;
      btn.textContent = "…";
    }

    try {
      const cartAdd = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || "/cart/add.js";
      const res = await fetch(cartAdd, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id, quantity: 1 }],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.description || data.message || "cart_add_failed");
      }

      markCartAdded(item);

      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
      document.dispatchEvent(
        new CustomEvent("on:cart:add", {
          bubbles: true,
          detail: { variantId: id },
        }),
      );

      const drawer = document.querySelector("cart-drawer");
      if (drawer) {
        if (typeof drawer.refreshCartDrawer === "function") {
          await drawer.refreshCartDrawer();
        }
        if (typeof drawer.open === "function") {
          drawer.open();
        }
      }

      if (btn) {
        btn.textContent = "Added to cart";
        btn.classList.add("is-active");
        btn.disabled = true;
      }
    } catch (err) {
      console.error("[Canopy Wishlist] add to cart failed", err);
      if (btn) {
        btn.textContent = cartButtonLabel(item);
        btn.disabled = false;
        btn.classList.toggle("is-active", isCartAdded(item));
      }
    }
  }

  async function addItem(payload, { notify = false } = {}) {
    await ensureSession();
    await identifyLoggedInCustomer();
    let list = await ensureServerList(activeList());
    if (!list) throw new Error("no_list");
    const hints = customerHints();

    const existing = (list.items || []).find(
      (i) => i.variantGid === payload.variantGid,
    );
    if (existing) {
      if (notify) existing.notifyBackInStock = true;
      saveLocal();
      if (state.apiOnline && !isLocalId(list.id)) {
        try {
          const target = await resolveServerItem(list.id, existing.id);
          if (target) {
            if (notify || target.item.notifyBackInStock) {
              await api(
                `lists/${target.list.id}/items/${target.item.id}/notify`,
                {
                  method: "POST",
                  body: {
                    enabled: Boolean(notify || target.item.notifyBackInStock),
                    shop: SHOP,
                    ...hints,
                  },
                },
              );
            }
            if (notify) target.item.notifyBackInStock = true;
          }
        } catch (err) {
          console.warn("[Canopy Wishlist] notify sync failed", err);
        }
      }
      updateHeart();
      syncPdpButtons();
      renderDrawer();
      return { list, item: existing };
    }

    const localItem = {
      id: "item-" + crypto.randomUUID().slice(0, 10),
      productGid: payload.productGid,
      variantGid: payload.variantGid,
      variantId: payload.variantId || String(payload.variantGid || "").split("/").pop(),
      handle: payload.handle,
      title: payload.title,
      imageUrl: payload.imageUrl,
      productUrl: payload.productUrl,
      price:
        payload.price != null && payload.price !== ""
          ? Number(payload.price)
          : null,
      currency: "USD",
      available: payload.available !== false,
      notifyBackInStock: notify,
      createdAt: new Date().toISOString(),
    };

    list.items = [localItem, ...(list.items || [])];
    saveLocal();
    updateHeart();
    syncPdpButtons();
    renderDrawer();

    if (state.apiOnline) {
      try {
        list = (await ensureServerList(list)) || list;
        if (isLocalId(list.id)) {
          throw new Error("no_server_list");
        }
        const data = await api(`lists/${list.id}/items`, {
          method: "POST",
          body: {
            ...payload,
            notifyBackInStock: Boolean(notify),
            shop: SHOP,
            ...hints,
          },
        });
        if (data.list) {
          const prevId = list.id;
          state.lists = state.lists.map((l) =>
            l.id === prevId ? data.list : l,
          );
          if (!state.lists.find((l) => l.id === data.list.id)) {
            state.lists = [
              data.list,
              ...state.lists.filter((l) => l.id !== prevId && !isLocalId(l.id)),
            ];
          }
          state.activeListId = data.list.id;
          if (data.identity?.email) state.email = data.identity.email;
          saveLocal();
          updateHeart();
          syncPdpButtons();
          renderDrawer();
        }
      } catch (err) {
        console.error("[Canopy Wishlist] add failed to sync", err);
        // Ghost list ids after wipe → rebuild session/list and retry once.
        if (
          err?.status === 401 ||
          err?.status === 404 ||
          err?.data?.error === "unauthorized" ||
          err?.data?.error === "not_found"
        ) {
          try {
            clearAuthToken();
            resetClientWishlistState();
            await tryApiSession();
            list = await ensureServerList({ name: "My Wishlist" });
            if (list && !isLocalId(list.id)) {
              const data = await api(`lists/${list.id}/items`, {
                method: "POST",
                body: {
                  ...payload,
                  notifyBackInStock: Boolean(notify),
                  shop: SHOP,
                  ...customerHints(),
                },
              });
              if (data.list) {
                state.lists = [data.list];
                state.activeListId = data.list.id;
                saveLocal();
                updateHeart();
                syncPdpButtons();
                renderDrawer();
              }
            }
          } catch (retryErr) {
            console.error("[Canopy Wishlist] add retry failed", retryErr);
          }
        }
      }
    }

    return { list: activeList() || list, item: localItem };
  }

  async function syncListsFromApi() {
    if (!state.token && !(await tryApiSession())) return false;
    if (!state.apiOnline) return false;
    try {
      const data = await api("lists");
      if (data.lists?.length) {
        state.lists = data.lists;
        if (!state.lists.find((l) => l.id === state.activeListId)) {
          state.activeListId = state.lists[0].id;
        }
        state.email = data.identity?.email || state.email;
        saveLocal();
        return true;
      }
    } catch (_) {
      state.apiOnline = false;
    }
    return false;
  }

  async function resolveServerItem(listId, itemId) {
    let list = state.lists.find((l) => l.id === listId);
    let item = list?.items?.find((i) => i.id === itemId);
    const variantGid = item?.variantGid;
    const needsSync =
      !item ||
      String(listId).startsWith("local-") ||
      String(itemId).startsWith("item-");

    if (needsSync) {
      await syncListsFromApi();
      if (variantGid) {
        for (const l of state.lists) {
          const found = (l.items || []).find((i) => i.variantGid === variantGid);
          if (found) {
            list = l;
            item = found;
            break;
          }
        }
      } else {
        list = state.lists.find((l) => l.id === listId);
        item = list?.items?.find((i) => i.id === itemId);
      }
    }

    if (
      !list ||
      !item ||
      String(list.id).startsWith("local-") ||
      String(item.id).startsWith("item-")
    ) {
      return null;
    }
    return { list, item };
  }

  async function removeItem(listId, itemId) {
    const list = state.lists.find((l) => l.id === listId);
    const removing = list?.items?.find((i) => i.id === itemId);
    const variantGid = removing?.variantGid;

    // Resolve server ids before mutating local state
    let target = null;
    if (removing) {
      target = await resolveServerItem(listId, itemId);
      if (!target && variantGid) {
        await syncListsFromApi();
        for (const l of state.lists) {
          const found = (l.items || []).find((i) => i.variantGid === variantGid);
          if (found) {
            target = { list: l, item: found };
            break;
          }
        }
      }
    }

    if (list) {
      list.items = (list.items || []).filter((i) => i.id !== itemId);
      if (variantGid) {
        list.items = list.items.filter((i) => i.variantGid !== variantGid);
      }
      saveLocal();
    }
    updateHeart();
    renderDrawer();
    syncPdpButtons();

    if (target && state.apiOnline) {
      try {
        await api(`lists/${target.list.id}/items/${target.item.id}/remove`, {
          method: "POST",
          body: { shop: SHOP, ...customerHints() },
        });
        await syncListsFromApi();
        updateHeart();
        renderDrawer();
        syncPdpButtons();
      } catch (_) {}
    }
  }

  async function setNotify(listId, itemId, enabled) {
    const list = state.lists.find((l) => l.id === listId);
    const item = list?.items?.find((i) => i.id === itemId);
    if (!item) return;

    await ensureSession();
    let target = await resolveServerItem(listId, itemId);
    if (!target && item.variantGid) {
      await syncListsFromApi();
      for (const l of state.lists) {
        const found = (l.items || []).find(
          (i) => i.variantGid === item.variantGid,
        );
        if (found) {
          target = { list: l, item: found };
          break;
        }
      }
    }

    item.notifyBackInStock = Boolean(enabled);
    if (target) target.item.notifyBackInStock = Boolean(enabled);
    saveLocal();
    updateHeart();
    syncPdpButtons();
    renderDrawer();

    if (target && state.apiOnline) {
      try {
        await api(`lists/${target.list.id}/items/${target.item.id}/notify`, {
          method: "POST",
          body: { enabled: Boolean(enabled), shop: SHOP, ...customerHints() },
        });
      } catch (err) {
        console.error("[Canopy Wishlist] setNotify failed", err);
        item.notifyBackInStock = !enabled;
        if (target) target.item.notifyBackInStock = !enabled;
        saveLocal();
        updateHeart();
        syncPdpButtons();
        renderDrawer();
      }
    }
  }

  async function renameList(listId, name) {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;

    const list = state.lists.find((l) => l.id === listId);
    if (!list || list.name === trimmed) return;

    list.name = trimmed;
    saveLocal();
    renderDrawer();

    if (state.apiOnline && !String(listId).startsWith("local-")) {
      try {
        const data = await api(`lists/${listId}`, {
          method: "PATCH",
          body: { name: trimmed },
        });
        if (data.list) {
          state.lists = state.lists.map((l) =>
            l.id === listId ? data.list : l,
          );
          saveLocal();
          renderDrawer();
        }
      } catch (_) {}
    }
  }

  async function moveItem(fromListId, itemId, toListId) {
    if (!toListId || toListId === fromListId) return;
    const from = state.lists.find((l) => l.id === fromListId);
    const to = state.lists.find((l) => l.id === toListId);
    if (!from || !to) return;
    const item = (from.items || []).find((i) => i.id === itemId);
    if (!item) return;

    from.items = (from.items || []).filter((i) => i.id !== itemId);
    const exists = (to.items || []).find(
      (i) => i.variantGid === item.variantGid,
    );
    if (exists) {
      if (item.notifyBackInStock) exists.notifyBackInStock = true;
    } else {
      to.items = [item, ...(to.items || [])];
    }
    state.activeListId = toListId;
    saveLocal();
    updateHeart();
    syncPdpButtons();
    renderDrawer();

    if (
      state.apiOnline &&
      !String(fromListId).startsWith("local-") &&
      !String(toListId).startsWith("local-")
    ) {
      try {
        await api(`lists/${fromListId}/items/${itemId}/move`, {
          method: "POST",
          body: { targetListId: toListId, shop: SHOP },
        });
        const data = await api("lists");
        if (data.lists?.length) {
          state.lists = data.lists;
          state.activeListId = toListId;
          saveLocal();
          updateHeart();
          syncPdpButtons();
          renderDrawer();
        }
      } catch (_) {}
    }
  }

  async function refreshItemsAvailability(items) {
    const list = (items || []).slice(0, Math.max(state.drawerVisibleCount, DRAWER_PAGE_SIZE));
    const concurrency = 3;
    let index = 0;
    const runOne = async () => {
      while (index < list.length) {
        const item = list[index++];
        if (!item.handle) continue;
        try {
          const res = await fetch(
            `/products/${encodeURIComponent(item.handle)}.js`,
          );
          if (!res.ok) continue;
          const product = await res.json();
          const vid = variantNumericId(item);
          const variant = (product.variants || []).find(
            (v) => Number(v.id) === Number(vid),
          );
          if (!variant) continue;
          item.available = Boolean(variant.available);
          item.price = Number(variant.price) / 100;
          item.title = product.title || item.title;
          if (variant.featured_image?.src) {
            item.imageUrl = variant.featured_image.src;
          } else if (product.featured_image) {
            item.imageUrl = product.featured_image;
          }
          if (
            state.apiOnline &&
            item.id &&
            !String(item.id).startsWith("item-")
          ) {
            const owner = state.lists.find((l) =>
              (l.items || []).some((i) => i.id === item.id),
            );
            if (owner && !String(owner.id).startsWith("local-")) {
              api(`lists/${owner.id}/items/${item.id}`, {
                method: "PATCH",
                body: {
                  available: item.available,
                  price: item.price,
                  imageUrl: item.imageUrl,
                  title: item.title,
                },
              }).catch(() => {});
            }
          }
        } catch (_) {}
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(concurrency, list.length) }, runOne),
    );
    saveLocal();
  }

  async function deleteList(listId) {
    const target = state.lists.find((l) => l.id === listId);
    if (!target) return;

    const remaining = state.lists.filter((l) => l.id !== listId);
    if (remaining.length === 0) {
      const id = "local-" + crypto.randomUUID().slice(0, 8);
      state.lists = [
        {
          id,
          name: "My Wishlist",
          listType: "personal",
          items: [],
        },
      ];
      state.activeListId = id;
    } else {
      state.lists = remaining;
      if (state.activeListId === listId) {
        state.activeListId = remaining[0].id;
      }
    }
    saveLocal();
    updateHeart();
    syncPdpButtons();
    renderDrawer();

    if (state.apiOnline && !String(listId).startsWith("local-")) {
      try {
        await api(`lists/${listId}`, { method: "DELETE" });
        if (state.apiOnline) {
          const data = await api("lists");
          if (data.lists?.length) {
            state.lists = data.lists;
            if (!state.lists.find((l) => l.id === state.activeListId)) {
              state.activeListId = state.lists[0].id;
            }
            saveLocal();
            updateHeart();
            syncPdpButtons();
            renderDrawer();
          }
        }
      } catch (_) {}
    }
  }

  function pdpPayload(root) {
    return {
      productGid: root.dataset.productGid,
      variantGid: root.dataset.variantGid,
      variantId: root.dataset.variantId,
      handle: root.dataset.handle,
      title: root.dataset.title,
      price: root.dataset.price,
      imageUrl: root.dataset.image,
      productUrl: root.dataset.url,
      available: root.dataset.available === "true",
    };
  }

  function syncPdpButtons() {
    document.querySelectorAll("[data-cpc-wishlist-pdp]").forEach((root) => {
      const found = findItem(root.dataset.variantGid);
      const addBtn = root.querySelector("[data-cpc-wl-add]");
      const notifyBtn = root.querySelector("[data-cpc-wl-notify]");
      const label = root.querySelector("[data-cpc-wl-add-label]");
      const notifyLabel = root.querySelector("[data-cpc-wl-notify-label]");
      const available = root.dataset.available === "true";
      const notified = Boolean(found?.item?.notifyBackInStock);

      if (notifyBtn) {
        notifyBtn.hidden = available;
      }
      root.classList.toggle("cpc-wl-pdp--add-only", available);

      if (addBtn) {
        addBtn.classList.toggle("is-active", Boolean(found));
        if (label) label.textContent = found ? "Wishlisted" : "Add To Wishlist";
      }
      if (notifyBtn) {
        notifyBtn.classList.toggle("is-active", notified);
        notifyBtn.removeAttribute("disabled");
        if (notifyLabel) {
          notifyLabel.textContent = notified
            ? "You'll Be Notified When Back In Stock"
            : "Notify Me When Back In Stock";
        } else {
          notifyBtn.textContent = notified
            ? "You'll Be Notified When Back In Stock"
            : "Notify Me When Back In Stock";
        }
      }
    });
  }

  function syncPdpFromVariant(variant) {
    if (!variant) return;
    document.querySelectorAll("[data-cpc-wishlist-pdp]").forEach((root) => {
      const available = Boolean(variant.available);
      root.dataset.available = available ? "true" : "false";
      root.dataset.variantId = String(variant.id);
      root.dataset.variantGid = `gid://shopify/ProductVariant/${variant.id}`;
      if (variant.price != null) {
        root.dataset.price = (Number(variant.price) / 100).toFixed(2);
      }
      if (variant.featured_image?.src) {
        root.dataset.image = variant.featured_image.src;
      }
    });
    syncPdpButtons();
  }

  function bindPdp(root) {
    if (root.dataset.cpcBound === "1") return;
    root.dataset.cpcBound = "1";

    const addBtn = root.querySelector("[data-cpc-wl-add]");
    const notifyBtn = root.querySelector("[data-cpc-wl-notify]");
    const emailWrap = root.querySelector("[data-cpc-wl-email]");
    const emailInput = root.querySelector("[data-cpc-wl-email-input]");
    const emailConfirm = root.querySelector("[data-cpc-wl-email-confirm]");

    addBtn?.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const found = findItem(root.dataset.variantGid);
        if (found) {
          await removeItem(found.list.id, found.item.id);
          return;
        }
        await addItem(pdpPayload(root), { notify: false });
        openDrawer();
      } catch (err) {
        console.error("[Canopy Wishlist] add failed", err);
      }
    });

    notifyBtn?.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (notifyBtn.disabled || notifyBtn.classList.contains("is-active")) return;

      const run = async () => {
        const found = findItem(root.dataset.variantGid);
        if (found) {
          found.item.notifyBackInStock = true;
          saveLocal();
          syncPdpButtons();
          if (state.apiOnline && !String(found.list.id).startsWith("local-")) {
            try {
              await api(`lists/${found.list.id}/items/${found.item.id}/notify`, {
                method: "POST",
                body: { enabled: true, shop: SHOP, ...customerHints() },
              });
            } catch (_) {}
          }
        } else {
          await addItem(pdpPayload(root), { notify: true });
        }
        openDrawer();
      };

      try {
        if (!state.email) {
          state.pendingNotify = run;
          if (emailWrap) emailWrap.hidden = false;
          return;
        }
        await run();
      } catch (err) {
        console.error("[Canopy Wishlist] notify failed", err);
      }
    });

    emailConfirm?.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const email = emailInput?.value?.trim();
      if (!email) return;
      state.email = email;
      saveLocal();
      if (state.apiOnline) {
        try {
          await api("identify", { method: "POST", body: { email, shop: SHOP } });
        } catch (_) {}
      }
      if (emailWrap) emailWrap.hidden = true;
      if (state.pendingNotify) {
        const fn = state.pendingNotify;
        state.pendingNotify = null;
        await fn();
      }
    });
  }

  function drawerRoot() {
    return document.querySelector("[data-cpc-wishlist-drawer]");
  }

  function drawerIsVisible() {
    const root = drawerRoot();
    return Boolean(root && !root.hidden);
  }

  function sizedImageUrl(url) {
    const src = String(url || "");
    if (!src) return "";
    try {
      const parsed = new URL(src, window.location.origin);
      if (!/shopify/i.test(parsed.hostname + parsed.pathname)) return src;
      parsed.searchParams.set("width", "160");
      parsed.searchParams.set("height", "160");
      parsed.searchParams.set("crop", "center");
      return parsed.toString();
    } catch (_) {
      return src;
    }
  }

  function openDrawer() {
    const root = drawerRoot();
    if (!root) return;
    state.drawerVisibleCount = DRAWER_PAGE_SIZE;
    root.hidden = false;
    requestAnimationFrame(() => root.classList.add("is-open"));
    document.body.classList.add("cpc-wl-drawer-open");
    renderDrawer();
    const items = activeList()?.items || [];
    syncCartAddedFromCart().then(() => {
      if (drawerIsVisible()) renderDrawer();
    });
    refreshItemsAvailability(items).then(() => {
      if (drawerIsVisible()) renderDrawer();
    });
  }

  function closeDrawer() {
    const root = drawerRoot();
    if (!root) return;
    root.classList.remove("is-open");
    document.body.classList.remove("cpc-wl-drawer-open");
    setTimeout(() => {
      if (!root.classList.contains("is-open")) root.hidden = true;
    }, 300);
  }

  function accountLoginHref() {
    const cfg = customerConfig();
    const base =
      cfg.accountLoginUrl ||
      (window.theme && window.theme.routes && window.theme.routes.accountLoginUrl) ||
      "/account/login";
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const join = base.includes("?") ? "&" : "?";
    return `${base}${join}return_url=${encodeURIComponent(returnTo)}`;
  }

  function renderAccountFooter() {
    const root = drawerRoot();
    const footer = root?.querySelector("[data-cpc-wl-account]");
    if (!footer) return;

    const cfg = customerConfig();
    const email = cfg.customerEmail || state.email || null;
    const name = cfg.customerName || (email ? email.split("@")[0] : null);
    const loggedIn = Boolean(cfg.customerId || cfg.customerEmail);

    if (loggedIn) {
      const initial = String(name || email || "A")
        .trim()
        .charAt(0)
        .toUpperCase();
      const accountUrl = cfg.accountUrl || "/account";
      footer.innerHTML = `
        <div class="cpc-wl-drawer__account-card">
          <div class="cpc-wl-drawer__account-user">
            <span class="cpc-wl-drawer__account-avatar" aria-hidden="true">${initial}</span>
            <div class="cpc-wl-drawer__account-meta">
              <div class="cpc-wl-drawer__account-name">${String(name || "Account").replace(/</g, "&lt;")}</div>
              <div class="cpc-wl-drawer__account-email">${String(email || "").replace(/</g, "&lt;")}</div>
            </div>
          </div>
          <a class="cpc-wl-drawer__account-btn" href="${accountUrl}">Account</a>
        </div>`;
      return;
    }

    footer.innerHTML = `
      <div class="cpc-wl-drawer__account-card">
        <a class="cpc-wl-drawer__account-btn" href="${accountLoginHref()}">Login</a>
      </div>`;
  }

  function renderDrawer() {
    const root = drawerRoot();
    if (!root) return;
    const switcher = root.querySelector("[data-cpc-wl-list-switcher]");
    const grid = root.querySelector("[data-cpc-wl-grid]");
    const empty = root.querySelector("[data-cpc-wl-empty]");
    const itemsHeading = root.querySelector("[data-cpc-wl-items-heading]");
    if (!grid || !switcher) return;

    if (itemsHeading) itemsHeading.textContent = "Wishlist";

    renderAccountFooter();

    ensureLocalLists();

    switcher.innerHTML = "";
    {
      const select = document.createElement("select");
      select.className = "cpc-wl-select";
      select.setAttribute("aria-label", "My Lists");
      if (!state.lists.length) {
        const opt = document.createElement("option");
        opt.value = "";
        opt.textContent = "—";
        select.appendChild(opt);
        select.disabled = true;
      } else {
        state.lists.forEach((list) => {
          const opt = document.createElement("option");
          opt.value = list.id;
          opt.textContent = list.name;
          if (list.id === state.activeListId) opt.selected = true;
          select.appendChild(opt);
        });
        select.addEventListener("change", () => {
          state.activeListId = select.value;
          state.drawerVisibleCount = DRAWER_PAGE_SIZE;
          saveLocal();
          renderDrawer();
        });
      }
      switcher.appendChild(select);
    }

    const active = activeList();
    const items = active?.items || [];
    grid.innerHTML = "";
    if (empty) empty.hidden = items.length > 0;
    if (root.hidden) return;

    const visibleItems = items.slice(0, state.drawerVisibleCount);
    visibleItems.forEach((item) => {
      const href = item.productUrl || `/products/${item.handle}`;
      const price =
        item.price != null && item.price !== ""
          ? `$${Number(item.price).toFixed(2)}`
          : "";
      const soldOut = item.available === false;
      const row = document.createElement("article");
      row.className = "cpc-wl-row";

      let actionsHtml = "";
      const otherLists = state.lists.filter((l) => l.id !== active.id);
        const moveHtml =
          otherLists.length > 0
            ? `<select class="cpc-wl-row__move" data-move aria-label="Move to list">
                <option value="">Move…</option>
                ${otherLists
                  .map(
                    (l) =>
                      `<option value="${l.id}">${l.name.replace(/</g, "&lt;")}</option>`,
                  )
                  .join("")}
              </select>`
            : "";
        actionsHtml = `
          ${
            soldOut
              ? item.notifyBackInStock
                ? `<button type="button" class="cpc-wl-btn cpc-wl-btn--ghost cpc-wl-btn--sm" data-cancel-notify>Cancel notify</button>`
                : `<button type="button" class="cpc-wl-btn cpc-wl-btn--sm" data-notify>Notify me</button>`
              : `<button type="button" class="cpc-wl-btn cpc-wl-btn--sm${isCartAdded(item) ? " is-active" : ""}" data-cart${isCartAdded(item) ? " disabled" : ""}>${cartButtonLabel(item)}</button>`
          }
          ${moveHtml}
          <button type="button" class="cpc-wl-btn cpc-wl-btn--ghost cpc-wl-btn--sm" data-remove>Remove</button>
        `;

      row.innerHTML = `
        <a href="${href}"><img src="${sizedImageUrl(item.imageUrl)}" alt="" width="72" height="72" loading="lazy" decoding="async"></a>
        <div class="cpc-wl-row__body">
          <a class="cpc-wl-row__title" href="${href}">${item.title || item.handle || ""}</a>
          <div class="cpc-wl-row__meta">${price}${soldOut ? " · Sold out" : ""}${item.notifyBackInStock ? " · Notify" : ""}</div>
          <div class="cpc-wl-row__actions">${actionsHtml}</div>
        </div>`;

      row.querySelector("[data-remove]")?.addEventListener("click", () =>
        removeItem(active.id, item.id),
      );
      row.querySelector("[data-cart]")?.addEventListener("click", (e) =>
        addToCart(item, e.currentTarget),
      );
      row.querySelector("[data-cancel-notify]")?.addEventListener("click", () =>
        setNotify(active.id, item.id, false),
      );
      row.querySelector("[data-notify]")?.addEventListener("click", async () => {
        const run = () => setNotify(active.id, item.id, true);
        if (!state.email) {
          state.pendingNotify = run;
          openEmailPrompt();
          return;
        }
        await run();
      });
      row.querySelector("[data-move]")?.addEventListener("change", (e) => {
        const toId = e.target.value;
        if (!toId) return;
        moveItem(active.id, item.id, toId);
      });

      grid.appendChild(row);
    });

    if (items.length > visibleItems.length) {
      const more = document.createElement("button");
      more.type = "button";
      more.className = "cpc-wl-btn cpc-wl-btn--ghost cpc-wl-more";
      more.textContent = `Show more (${items.length - visibleItems.length})`;
      more.addEventListener("click", () => {
        state.drawerVisibleCount += DRAWER_PAGE_SIZE;
        renderDrawer();
        refreshItemsAvailability(activeList()?.items || []).then(() => {
          if (drawerIsVisible()) renderDrawer();
        });
      });
      grid.appendChild(more);
    }
  }

  function bindDrawer() {
    const root = drawerRoot();
    if (!root || root.dataset.cpcBound === "1") return;
    root.dataset.cpcBound = "1";

    root.querySelectorAll("[data-cpc-wl-close]").forEach((el) => {
      el.addEventListener("click", closeDrawer);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeDrawer();
    });

    root.querySelector("[data-cpc-wl-new-list]")?.addEventListener("click", () => {
      const name = window.prompt("Name");
      if (!name) return;
      const id = "local-" + crypto.randomUUID().slice(0, 8);
      state.lists.push({
        id,
        name,
        listType: "personal",
        items: [],
      });
      state.activeListId = id;
      saveLocal();
      renderDrawer();
      if (state.apiOnline) {
        api("lists", { method: "POST", body: { name, shop: SHOP } })
          .then((data) => {
            if (data.list) {
              state.lists = state.lists.map((l) =>
                l.id === id ? data.list : l,
              );
              state.activeListId = data.list.id;
              saveLocal();
              renderDrawer();
            }
          })
          .catch(() => {});
      }
    });

    root.querySelector("[data-cpc-wl-rename-list]")?.addEventListener("click", async () => {
      const active = activeList();
      if (!active) return;
      const name = window.prompt("Name", active.name);
      if (name == null) return;
      await renameList(active.id, name);
    });


    root.querySelector("[data-cpc-wl-delete-list]")?.addEventListener("click", async () => {
      const active = activeList();
      if (!active) return;
      const ok = window.confirm(`Delete “${active.name}”?`);
      if (!ok) return;
      await deleteList(active.id);
    });
  }

  function bindHearts() {
    document.querySelectorAll("[data-cpc-wl-open]").forEach((btn) => {
      if (btn.dataset.cpcBound === "1") return;
      btn.dataset.cpcBound = "1";
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        openDrawer();
      });
    });
  }

  function isWishlistOpenHref(href) {
    if (!href) return false;
    const value = String(href).trim().toLowerCase();
    if (
      value === "#wishlist" ||
      value === "#cpc-wishlist" ||
      value === "#cpc-wl-open" ||
      value.endsWith("#wishlist") ||
      value.endsWith("#cpc-wishlist")
    ) {
      return true;
    }
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      const path = url.pathname.replace(/\/$/, "");
      return (
        path === "/pages/my-wishlist" ||
        path === "/pages/wishlist" ||
        value.includes("#swym-list") ||
        value.includes("#swym-wishlist") ||
        value.includes("swym-wishlist") ||
        value.includes("/apps/swym") ||
        value.includes("wishlist-plus")
      );
    } catch (_) {
      return (
        value.includes("#swym-list") ||
        value.includes("#swym-wishlist") ||
        value.includes("swym-wishlist") ||
        value.includes("/apps/swym") ||
        value.includes("wishlist-plus")
      );
    }
  }

  function bindWishlistNavLinks() {
    if (document.documentElement.dataset.cpcWlNavBound === "1") return;
    document.documentElement.dataset.cpcWlNavBound = "1";
    document.addEventListener("click", (e) => {
      const link = e.target.closest("a[href]");
      if (!link) return;
      if (!isWishlistOpenHref(link.getAttribute("href"))) return;
      e.preventDefault();
      e.stopPropagation();
      openDrawer();
      try {
        if (window.location.hash && /wishlist/i.test(window.location.hash)) {
          history.replaceState(
            {},
            "",
            window.location.pathname + window.location.search,
          );
        }
      } catch (_) {}
    });
  }

  function openDrawerFromLocation() {
    const path = window.location.pathname.replace(/\/$/, "");
    const hash = (window.location.hash || "").toLowerCase();
    const shouldOpen =
      path === "/pages/my-wishlist" ||
      path === "/pages/wishlist" ||
      hash === "#wishlist" ||
      hash === "#cpc-wishlist" ||
      hash === "#cpc-wl-open";
    if (!shouldOpen) return;
    openDrawer();
    if (hash === "#wishlist" || hash === "#cpc-wishlist" || hash === "#cpc-wl-open") {
      try {
        history.replaceState(
          {},
          "",
          window.location.pathname + window.location.search,
        );
      } catch (_) {}
    }
  }

  function bindLegacyWishlistLinks() {
    bindWishlistNavLinks();
  }

  function parseCardVariants(btn) {
    const wrap = btn.closest(".cpc-wl-card-actions");
    const script = wrap?.querySelector("[data-cpc-wl-variants]");
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function buildPayloadFromVariant(btn, variant) {
    const base = cardPayloadFromBtn(btn);
    if (!variant) return base;
    const priceCents = Number(variant.price);
    const price =
      Number.isFinite(priceCents)
        ? (priceCents / 100).toFixed(2)
        : base.price;
    const titleBase = variant.productTitle || base.title;
    const optionTitle =
      variant.title && variant.title !== "Default Title" ? variant.title : "";
    return {
      ...base,
      variantGid: `gid://shopify/ProductVariant/${variant.id}`,
      variantId: String(variant.id),
      title: optionTitle ? `${titleBase} — ${optionTitle}` : titleBase,
      price,
      available: variant.available !== false,
      imageUrl: variant.image || base.imageUrl,
    };
  }

  function showVariantPicker(anchor, variants, onPick) {
    document.querySelectorAll(".cpc-wl-variant-picker").forEach((el) => el.remove());
    const picker = document.createElement("div");
    picker.className = "cpc-wl-variant-picker";
    picker.innerHTML = variants
      .map(
        (v) => `
      <button type="button" class="cpc-wl-variant-picker__option" data-variant-id="${v.id}">
        <span>${(v.title && v.title !== "Default Title" ? v.title : "Default").replace(/</g, "&lt;")}</span>
        <span>${v.available === false ? "Sold out" : ""}</span>
      </button>`,
      )
      .join("");
    const rect = anchor.getBoundingClientRect();
    picker.style.left = `${Math.min(rect.left, window.innerWidth - 220)}px`;
    picker.style.top = `${Math.min(rect.bottom + 6, window.innerHeight - 160)}px`;
    document.body.appendChild(picker);

    const close = (e) => {
      if (e && picker.contains(e.target)) return;
      picker.remove();
      document.removeEventListener("click", close, true);
    };
    picker.querySelectorAll("[data-variant-id]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.getAttribute("data-variant-id");
        const variant = variants.find((v) => String(v.id) === String(id));
        picker.remove();
        document.removeEventListener("click", close, true);
        onPick(variant);
      });
    });
    setTimeout(() => document.addEventListener("click", close, true), 0);
  }

  function bindCardHearts() {
    if (document.documentElement.dataset.cpcWlCardBound === "1") return;
    document.documentElement.dataset.cpcWlCardBound = "1";

    // Capture phase so notify never falls through to the heart handler
    document.addEventListener(
      "click",
      async (e) => {
        const notifyBtn = e.target.closest(
          "[data-cpc-wl-card-notify], .cpc-wl-card-notify",
        );
        if (notifyBtn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (
            notifyBtn.classList.contains("is-active") ||
            notifyBtn.dataset.cpcBusy === "1"
          ) {
            return;
          }
          notifyBtn.dataset.cpcBusy = "1";
          try {
            const variants = parseCardVariants(notifyBtn).filter(
              (v) => v.available === false,
            );
            const runWithPayload = (payload) => {
              const run = () => runNotify(payload);
              if (!state.email) {
                state.pendingNotify = run;
                openEmailPrompt();
                return Promise.resolve();
              }
              return run();
            };

            if (variants.length > 1) {
              showVariantPicker(notifyBtn, variants, async (variant) => {
                await runWithPayload(
                  buildPayloadFromVariant(notifyBtn, variant),
                );
              });
            } else {
              const variant = variants[0];
              await runWithPayload(
                variant
                  ? buildPayloadFromVariant(notifyBtn, variant)
                  : cardPayloadFromBtn(notifyBtn),
              );
            }
          } catch (err) {
            console.error("[Canopy Wishlist] card notify failed", err);
          } finally {
            notifyBtn.dataset.cpcBusy = "0";
          }
          return;
        }

        const btn = e.target.closest(
          "[data-cpc-wl-card-btn], .cpc-wl-card-heart",
        );
        if (!btn) return;
        if (btn.matches("[data-cpc-wl-card-notify], .cpc-wl-card-notify")) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (btn.dataset.cpcBusy === "1") return;
        btn.dataset.cpcBusy = "1";

        try {
          const found =
            findItem(btn.dataset.variantGid) ||
            findItemByProduct(btn.dataset.productGid);

          if (found) {
            await removeItem(found.list.id, found.item.id);
          } else {
            const variants = parseCardVariants(btn);
            if (variants.length > 1) {
              showVariantPicker(btn, variants, async (variant) => {
                await addItem(buildPayloadFromVariant(btn, variant), {
                  notify: false,
                });
              });
            } else {
              await addItem(
                variants[0]
                  ? buildPayloadFromVariant(btn, variants[0])
                  : cardPayloadFromBtn(btn),
                { notify: false },
              );
            }
          }
        } catch (err) {
          console.error("[Canopy Wishlist] card heart failed", err);
        } finally {
          btn.dataset.cpcBusy = "0";
        }
      },
      true,
    );

    if (typeof MutationObserver !== "undefined") {
      let timer = 0;
      const observer = new MutationObserver((mutations) => {
        const relevant = mutations.some((m) => {
          const target = m.target;
          if (!(target instanceof Element) && !(target instanceof Node)) {
            return false;
          }
          const el =
            target instanceof Element ? target : target.parentElement;
          if (!el) return false;
          if (el.closest("[data-cpc-wishlist-drawer], .cpc-wl-variant-picker")) {
            return false;
          }
          return true;
        });
        if (!relevant) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          observer.disconnect();
          try {
            syncCardHearts();
          } finally {
            if (document.body) {
              observer.observe(document.body, {
                childList: true,
                subtree: true,
              });
            }
          }
        }, 250);
      });
      if (document.body) {
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  async function bootUi() {
    bindDrawer();
    bindHearts();
    bindLegacyWishlistLinks();
    bindEmailPrompt();
    bindCardHearts();
    updateHeart();
    syncPdpButtons();
    document.querySelectorAll("[data-cpc-wishlist-pdp]").forEach(bindPdp);
  }

  async function init() {
    try {
      await ensureSession();
      await bootUi();
      document.addEventListener("on:variant:change", (event) => {
        syncPdpFromVariant(event.detail?.variant);
      });

      openDrawerFromLocation();
    } catch (err) {
      console.error("[Canopy Wishlist]", err);
      hydrateFromLocal();
      ensureLocalLists();
      await bootUi();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.CanopyWishlist = {
    open: openDrawer,
    close: closeDrawer,
    refresh: init,
    getState: () => ({ ...state }),
  };
})();
